/** Days before a deadline from which it is shown as due soon. */
export const DEADLINE_WARNING_DAYS = 30;

export type DeadlineState = "overdue" | "soon" | "ok";

export interface VehicleDeadline {
  key: string;
  label: string;
  /** Zero date number (UTC midnight ms) */
  date: number;
  state: DeadlineState;
}

type DeadlineFields = {
  nextInspectionDate?: number | null;
  nextServiceDate?: number | null;
  vignetteValidUntil?: number | null;
  insuranceValidUntil?: number | null;
};

const DEADLINES: { key: keyof DeadlineFields; label: string }[] = [
  { key: "nextInspectionDate", label: "MFK" },
  { key: "nextServiceDate", label: "Service" },
  { key: "vignetteValidUntil", label: "Vignette" },
  { key: "insuranceValidUntil", label: "Insurance" },
];

const DAY_MS = 86_400_000;

/** Today as a Zero date number (UTC midnight of the local calendar day). */
export function todayZeroDate(now = new Date()) {
  return Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
}

/** All set deadlines of a vehicle, soonest first. */
export function getVehicleDeadlines(
  vehicle: DeadlineFields,
  today = todayZeroDate(),
): VehicleDeadline[] {
  return DEADLINES.flatMap(({ key, label }) => {
    const date = vehicle[key];
    if (date === null || date === undefined) return [];
    const state: DeadlineState =
      date < today
        ? "overdue"
        : date <= today + DEADLINE_WARNING_DAYS * DAY_MS
          ? "soon"
          : "ok";
    return [{ key, label, date, state }];
  }).sort((a, b) => a.date - b.date);
}

/** Deadlines that need attention (overdue or due soon). */
export function getDueVehicleDeadlines(
  vehicle: DeadlineFields,
  today = todayZeroDate(),
) {
  return getVehicleDeadlines(vehicle, today).filter(
    (deadline) => deadline.state !== "ok",
  );
}
